/**
 * Applications from the public page.
 *
 * Listen, then decide. Both decisions email a real person, and neither can
 * be taken back, so each asks once more before it goes — naming who is about
 * to be emailed. The internal note never leaves this screen.
 *
 * A decision and its email are separate on the server: if SES fails, the
 * decision still stands and the row says so, with a button to send it again.
 */
import { useCallback, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { BackOfficeNav } from "../components/BackOfficeNav";
import { Meter, MeterRow } from "../components/Meter";
import { Pagination } from "../components/Pagination";
import { RefreshButton } from "../components/RefreshButton";
import { ReviewSignIn } from "../components/ReviewSignIn";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { useToast } from "../hooks/useToast";
import { applicationAdmin, type ReviewCreds } from "../api/endpoints";
import { clearReviewCredentials, readReviewCredentials } from "../lib/session";
import { isCredentialFailure } from "../lib/backOffice";
import { formatDate, formatDuration } from "../lib/validation";
import { useConfig } from "../store/config";
import type { ApplicationCard, ApplicationList, ApplicationStatus } from "../types/api";

const FILTERS: Array<{ id: ApplicationStatus | "all"; label: string }> = [
  { id: "pending", label: "To review" },
  { id: "accepted", label: "Accepted" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

type Pending = { id: string; decision: "accepted" | "rejected" } | null;

export function AdminApplications() {
  const toast = useToast();
  const [creds, setCreds] = useState<ReviewCreds | null>(() => readReviewCredentials());
  const [data, setData] = useState<ApplicationList | null>(null);
  const [filter, setFilter] = useState<ApplicationStatus | "all">("pending");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [page, setPage] = useState(1);

  /**
   * Every way the filter changes goes through here. The chips are not
   * the only one — the "emails didn't send" callout switches it too, and
   * a reset wired only into the chips leaves that path showing page 2 of
   * a one-page list, which reads as "nothing here".
   */
  function changeFilter(next: ApplicationStatus | "all") {
    setFilter(next);
    setPage(1);
    setConfirming(null);
  }
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Pending>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!creds) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadError(null);
        const result = await applicationAdmin.list(creds, {
          status: filter === "all" ? undefined : filter,
          page,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (isCredentialFailure(err)) {
          clearReviewCredentials();
          setCreds(null);
        } else {
          setLoadError((err as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [creds, filter, nonce, page]);

  // Never mid-decision or with a note half-typed: a refresh would reorder
  // the list under whoever is about to click.
  const typing = Object.values(notes).some((note) => note.trim() !== "");
  useRefreshOnFocus(reload, { enabled: busy === null && confirming === null && !typing });

  async function decide(application: ApplicationCard, decision: "accepted" | "rejected") {
    if (!creds) return;
    setBusy(application.application_id);
    try {
      const result = await applicationAdmin.decide(creds, application.application_id, {
        decision,
        note: notes[application.application_id]?.trim() || undefined,
      });
      const delivered = result.email_delivery?.status;
      toast.show(
        delivered === "failed"
          ? `${decision === "accepted" ? "Accepted" : "Rejected"} — but the email didn't send. Use "Resend email".`
          : delivered === "skipped"
            ? `${decision === "accepted" ? "Accepted" : "Rejected"}. Email is switched off here, so nothing was sent.`
            : decision === "accepted"
              ? `Accepted — invite sent to ${application.email}.`
              : `Rejected — ${application.email} has been told.`,
        delivered === "failed" ? "error" : "success",
      );
      setNotes((previous) => ({ ...previous, [application.application_id]: "" }));
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
      if (isCredentialFailure(err)) {
        clearReviewCredentials();
        setCreds(null);
      } else {
        // Most likely somebody else decided it first; show them the truth.
        reload();
      }
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  async function resend(application: ApplicationCard) {
    if (!creds) return;
    setBusy(application.application_id);
    try {
      const result = await applicationAdmin.resend(creds, application.application_id);
      toast.show(
        result.email_delivery?.status === "sent"
          ? `Sent again to ${application.email}.`
          : `Still couldn't send: ${result.email_delivery?.error ?? "unknown error"}`,
        result.email_delivery?.status === "sent" ? "success" : "error",
      );
      reload();
    } catch (err) {
      toast.show((err as Error).message, "error");
    } finally {
      setBusy(null);
    }
  }

  if (!creds) return <ReviewSignIn onSubmit={setCreds} />;

  return (
    <Layout showNav={false} showAccount={false} title="Applications">
      <section className="section">
        <div className="wrap">
          <BackOfficeNav />

          <div className="row-between">
            <div className="eyebrow">Applications</div>
            <RefreshButton onClick={reload} busy={loading} />
          </div>
          <h1 className="display" style={{ fontSize: "clamp(22px,4vw,32px)", margin: "0 0 18px" }}>
            {data ? `${data.counts.pending} to review` : "Applications"}
          </h1>

          <MeterRow>
            <Meter label="To review" value={data?.counts.pending.toLocaleString()} />
            <Meter label="Accepted" value={data?.counts.accepted.toLocaleString()} />
            <Meter label="Rejected" value={data?.counts.rejected.toLocaleString()} />
          </MeterRow>

          {(data?.email_failed ?? 0) > 0 && (
            <button
              type="button"
              className="rerecord-callout"
              onClick={() => changeFilter("all")}
            >
              <b>
                {data?.email_failed} decision email{data?.email_failed === 1 ? "" : "s"} didn't
                send
              </b>
              <span>The decisions stand — find the rows marked "not sent" and resend.</span>
            </button>
          )}

          <div className="filter-row mt-24">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`chip ${filter === entry.id ? "chip-active" : ""}`}
                onClick={() => changeFilter(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          {loading && !data ? (
            <div className="stack mt-16" aria-busy="true" aria-label="Loading applications">
              {[0, 1].map((row) => (
                <div key={row} className="panel panel-tight log-skeleton">
                  <div className="skeleton-line skeleton-title" />
                  <div className="skeleton-line skeleton-meta" />
                  <div className="skeleton-line skeleton-text" />
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="panel empty mt-16">
              <h3>Couldn't load applications</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>{loadError}</p>
              <button type="button" className="btn btn-ghost mt-16" onClick={reload}>
                Try again
              </button>
            </div>
          ) : data && data.applications.length === 0 ? (
            <div className="panel empty mt-16">
              <h3>Nothing here</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                {filter === "pending"
                  ? "No applications are waiting. Share the apply link to bring people in."
                  : "Nothing with that status."}
              </p>
            </div>
          ) : (
            <div className="stack mt-16">
              {data?.applications.map((application) => (
                <ApplicationRow
                  key={application.application_id}
                  application={application}
                  busy={busy === application.application_id}
                  confirming={
                    confirming?.id === application.application_id ? confirming.decision : null
                  }
                  note={notes[application.application_id] ?? ""}
                  onNote={(value) =>
                    setNotes((previous) => ({ ...previous, [application.application_id]: value }))
                  }
                  onAsk={(decision) =>
                    setConfirming({ id: application.application_id, decision })
                  }
                  onCancel={() => setConfirming(null)}
                  onConfirm={(decision) => void decide(application, decision)}
                  onResend={() => void resend(application)}
                />
              ))}
            </div>
          )}

          {data && (
            <Pagination
              page={data.page}
              // This list has no has_more of its own, so derive it.
              hasMore={data.page * data.page_size < data.total}
              total={data.total}
              noun={data.total === 1 ? "application" : "applications"}
              busy={loading}
              onChange={setPage}
            />
          )}
        </div>
      </section>
    </Layout>
  );
}

function ApplicationRow({
  application,
  busy,
  confirming,
  note,
  onNote,
  onAsk,
  onCancel,
  onConfirm,
  onResend,
}: {
  application: ApplicationCard;
  busy: boolean;
  confirming: "accepted" | "rejected" | null;
  note: string;
  onNote: (value: string) => void;
  onAsk: (decision: "accepted" | "rejected") => void;
  onCancel: () => void;
  onConfirm: (decision: "accepted" | "rejected") => void;
  onResend: () => void;
}) {
  const pending = application.status === "pending";
  const delivery = application.email_delivery;
  const inviteDays = useConfig((state) => state.config.application.invite_days);

  return (
    <article className="panel application-row">
      <div className="row-between" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <div className="log-title">{application.name}</div>
          <div className="log-meta muted">
            {application.email}
            {application.country_name ? ` · ${application.country_name}` : ""}
            {application.rate ? ` · $${application.rate.formatted} a response` : ""} · applied{" "}
            {formatDate(application.created_at)}
            {application.source ? ` · via ${application.source}` : ""}
            {application.duration_seconds
              ? ` · ${formatDuration(application.duration_seconds)}`
              : ""}
          </div>
        </div>
        <span
          className={`pill ${
            application.status === "accepted"
              ? "pill-approved"
              : application.status === "rejected"
                ? "pill-rejected"
                : "pill-pending"
          }`}
        >
          {application.status === "pending"
            ? "To review"
            : application.status === "accepted"
              ? "Accepted"
              : "Rejected"}
        </span>
      </div>

      {application.ack_delivery?.status === "failed" && (
        <div className="review-note review-note-warn mt-8">
          <b>They weren't told we got it:</b> {application.ack_delivery.error ?? "the email failed"}.
          Deciding will email them as usual.
        </div>
      )}

      {application.previous_applications > 0 && (
        <div className="review-note review-note-warn mt-8">
          <b>Applied before:</b> {application.previous_applications} earlier application
          {application.previous_applications === 1 ? "" : "s"} from this address.
        </div>
      )}

      <div className="application-prompt">
        <div className="k">They were asked to read · {application.prompt.title}</div>
        <p>{application.prompt.text}</p>
      </div>

      {application.sample_url ? (
        <audio className="playback" controls src={application.sample_url} preload="none" />
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>No sample on file.</div>
      )}

      {pending ? (
        <>
          <div className="field mt-16">
            <label htmlFor={`note-${application.application_id}`}>
              Internal note (optional — never sent to them)
            </label>
            <input
              id={`note-${application.application_id}`}
              value={note}
              maxLength={1000}
              placeholder="e.g. clear and natural / heavy background noise"
              onChange={(event) => onNote(event.target.value)}
              disabled={busy}
            />
          </div>

          {confirming ? (
            <div className="application-confirm">
              <span>
                {confirming === "accepted"
                  ? `Send ${application.email} an invite valid for ${inviteDays} days` +
                    (application.rate ? `, at $${application.rate.formatted} a response?` : "?")
                  : `Tell ${application.email} we can't onboard them?`}{" "}
                This can't be undone.
              </span>
              <div className="btn-row">
                <button
                  type="button"
                  className={`btn btn-sm ${confirming === "accepted" ? "btn-primary" : "btn-danger-ghost"}`}
                  onClick={() => onConfirm(confirming)}
                  disabled={busy}
                >
                  {busy ? "Sending…" : confirming === "accepted" ? "Yes, accept" : "Yes, reject"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={onCancel}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="btn-row mt-16">
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto" }}
                onClick={() => onAsk("accepted")}
                disabled={busy}
              >
                Accept & send invite
              </button>
              <button
                type="button"
                className="btn btn-danger-ghost"
                style={{ width: "auto" }}
                onClick={() => onAsk("rejected")}
                disabled={busy}
              >
                Reject
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="application-outcome">
          <div className="muted" style={{ fontSize: 13 }}>
            {application.status === "accepted" ? "Accepted" : "Rejected"}
            {application.decided_by_name ? ` by ${application.decided_by_name}` : ""}
            {application.decided_at ? ` · ${formatDate(application.decided_at)}` : ""}
          </div>
          {application.note && (
            <div className="muted" style={{ fontSize: 13 }}>
              Note: {application.note}
            </div>
          )}
          {application.invite_code && (
            <div style={{ fontSize: 13 }}>
              Invite <span className="mono">{application.invite_code}</span>
              {application.invite_expires_at
                ? ` · valid until ${formatDate(application.invite_expires_at)}`
                : ""}
            </div>
          )}
          <div className="row-between mt-8">
            <span
              className={`application-delivery ${
                delivery?.status === "failed" ? "is-failed" : ""
              }`}
            >
              {delivery?.status === "sent"
                ? `Email sent${delivery.attempts > 1 ? ` (attempt ${delivery.attempts})` : ""}`
                : delivery?.status === "failed"
                  ? `Email not sent — ${delivery.error ?? "unknown error"}`
                  : delivery?.status === "skipped"
                    ? "Email switched off here — nothing was sent"
                    : "No email recorded"}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onResend}
              disabled={busy}
            >
              {busy ? "Sending…" : "Resend email"}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
